// This is the client path that already exists: `stackCellData` for the cell geometry (the read the
// Rx panel and the map both make) plus the fert engine run in the browser from the saved set's
// stored inputs — the same thing `loadRecSet` does when you open a saved Rx.
import { useQuery } from "@tanstack/react-query";
import { useRecSetsBus } from "../stores/recSetsBus";
import {
  newestSetFor,
  resolveSpreadRx,
  type SpreadRxNutrient,
} from "../lib/spreadRx";
import type { ResolvedSpreadRx } from "../lib/spreadRx";
import type { SavedRecSet } from "../lib/fertSets";

function spreadCellKey(set: SavedRecSet | null): string {
  const cells = set?.frozen?.cells;
  if (cells && typeof cells === "object") {
    return cells.gridId ?? cells.kind ?? "frozen";
  }
  return set?.gridId ?? "legacy";
}

export function useSpreadRx(
  fieldId: string | undefined,
  nutrient: SpreadRxNutrient | undefined,
  enabled: boolean,
) {
  // `newestSetFor` reads localStorage, which is not a React dependency — so without this the hook
  // keeps serving the snapshot it read on FIRST render and the server reconcile that just landed is
  // invisible until something unrelated re-renders. Same reason `recSetsBus` exists for the
  // dashboard pills; the spread family never subscribed.
  useRecSetsBus((s) => s.version);
  const set = fieldId && nutrient ? newestSetFor(fieldId, nutrient) : null;
  return useQuery({
    // the SET id is in the key: saving a newer Rx should re-resolve rather than serve the old
    // surface.
    // gridId is the cell identity. A legacy cache has none until the server
    // upgrade lands and the bus bumps this key.
    queryKey: ["spread-rx", fieldId, nutrient, set?.id, spreadCellKey(set)],
    enabled: Boolean(enabled && fieldId && nutrient && set),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<ResolvedSpreadRx> =>
      resolveSpreadRx(fieldId!, nutrient!, set!),
  });
}
